import React, { useEffect, useState } from "react";
import { cloneDeep, get, set } from "lodash";
import { COMPONENT_MODES, CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { isAddNewMode, isViewMode } from "../../../helpers/catalog-helper";
import CustomTextField from "../../../components/reusable-ui/textfield-box";
import { removeSpecialCharAndNumbers } from "../../../utils";
import CtaComponent from "../../../components/reusable-ui/cta";
import TaxDetailsModal from "./tax-details-modal";
import ConfirmationModal from "../../../components/reusable-ui/confirmation-modal";

interface Props {
  data?: any;
  modeConfig?: any;
  setModeConfig?: any;
  updateDataInParentState?: any;
  errorMessageMap?: any;
  taxAreaDataMap?: any;
  taxTypeDataMap?: any;
  taxAreaId?: string;
  isChangesMade?: boolean;
  onDiscardCtaClick?: any;
  onSaveCtaClick?: any;
}

const TaxAreaDetailsForm = (props: Props) => {
  const {
    data,
    taxAreaId = "",
    isChangesMade,
    taxAreaDataMap,
    modeConfig,
    setModeConfig,
    updateDataInParentState,
    errorMessageMap,
    onDiscardCtaClick,
    onSaveCtaClick,
    taxTypeDataMap,
  } = props;
  const [taxDetails, setTaxDetails] = useState<any>({});
  const [curTaxIndex, setCurTaxIndex] = useState<any>(-1);
  const [openTaxDetailsModal, setOpenTaxDetailsModal] = useState(false);
  const [taxErrorMap, setTaxErrorMap] = useState<any>({});
  const [isTaxEditMode, setTaxEditMode] = useState<boolean>(false);
  const [openTaxDeleteAlertPopup, setOpenTaxDeleteAlertPopup] = useState(false);
  const [selectedTaxTypeId, setSelectedTaxTypeId] = useState(""); //onDeleteTaxIconClick

  /**
   * This function will render the customTextField input with the help of textFieldConfig
   * @param textFieldConfig
   */
  function renderTextField(textFieldConfig: any): React.JSX.Element {
    return (
      <CustomTextField
        textBoxLabel={textFieldConfig.fieldLabel}
        placeholder={textFieldConfig.placeHolder}
        regexCheck={textFieldConfig.regexCheck || removeSpecialCharAndNumbers}
        handleChange={textFieldConfig.onSubmit}
        customClass={`min-h-[44px] flex items-center px-16 text-16 font-normal
          ${textFieldConfig.customClass || ""}`}
        parentClass={`${textFieldConfig.boxWidth || ""} ${
          textFieldConfig.parentClass || ""
        }`}
        value={textFieldConfig.value}
        rows={textFieldConfig.rows}
        isMandatory={!!textFieldConfig.isMandatory}
        errorMessage={errorMessageMap[textFieldConfig.key] || ""}
        iconClass={textFieldConfig.iconClass || ""}
        isDisabled={isViewMode(modeConfig?.type)}
        maxLength={textFieldConfig.maxLength}
      ></CustomTextField>
    );
  }

  function handleInputChange(value: any, fieldToUpdate: string) {
    updateDataInParentState(fieldToUpdate, value);
  }

  const onEditTaxIconClick = (taxData: any, index: number) => {
    setTaxDetails(cloneDeep(taxData));
    setCurTaxIndex(index);
    setOpenTaxDetailsModal(true);
    setTaxEditMode(true);
  };

  const onAddNewTaxCtaClick = () => {
    setTaxDetails({});
    setOpenTaxDetailsModal(true);
    setTaxEditMode(false);
  };

  const onTaxDetailsModalClose = () => {
    setOpenTaxDetailsModal(false);
    setTaxDetails({});
    setTaxErrorMap({});
  };

  const onTaxDetailsChange = (value: any, key: string) => {
    const dataToUpdate = cloneDeep(taxDetails) || {};
    set(dataToUpdate, key, value);
    setTaxDetails(dataToUpdate);
  };

  const validateDerivedTaxesData = () => {
    if (taxDetails?.derivedTaxes?.length) {
      let isValid = true;
      for (let derivedTaxData of taxDetails?.derivedTaxes) {
        if (derivedTaxData?.editMode) {
          isValid = false;
          break;
        }
      }

      if (isValid) {
        return true;
      }
      let derivedTaxesList: any = [];
      let dataToUpdate: any = {};
      let errMsgMap: any = {};
      for (let derivedTaxData of taxDetails?.derivedTaxes) {
        dataToUpdate = cloneDeep(derivedTaxData);
        if (!derivedTaxData.editMode) {
          derivedTaxesList.push(dataToUpdate);
          continue;
        }
        errMsgMap = {};
        if (!dataToUpdate?.name) {
          errMsgMap.name = "This field is mandatory";
        }

        if (!dataToUpdate?.percentage && dataToUpdate?.percentage !== 0) {
          errMsgMap.percentage = "This field is mandatory";
        }

        if (
          dataToUpdate?.name &&
          (dataToUpdate?.percentage || dataToUpdate?.percentage === 0)
        ) {
          errMsgMap.msg = "Save the secondary tax";
        }
        dataToUpdate.errMsgMap = errMsgMap;
        derivedTaxesList.push(dataToUpdate);
      }

      onTaxDetailsChange(derivedTaxesList, "derivedTaxes");
      return false;
    }
    return true;
  };

  const parseTaxes = (taxes: any) => {
    let parsedTax = cloneDeep(taxes);
    parsedTax.percentage = parseFloat(parsedTax?.percentage || 0);
    if (parsedTax.derivedTaxes?.length) {
      let derivedTaxes = parsedTax.derivedTaxes.map((tax: any) => {
        return { ...tax, percentage: parseFloat(tax.percentage) };
      });
      parsedTax.derivedTaxes = derivedTaxes;
    }
    return parsedTax;
  };

  const onSaveTaxCtaClick = () => {
    if (
      validateDerivedTaxesData() &&
      taxDetails?.taxTypeId &&
      (taxDetails?.percentage || taxDetails?.percentage === 0)
    ) {
      if (isTaxEditMode) {
        let taxList = data?.taxes?.map((taxData: any, index: number) => {
          return cloneDeep(curTaxIndex === index ? parseTaxes(taxDetails) : taxData);
        });
        handleInputChange(taxList, "taxes");
        setCurTaxIndex(-1);
      } else {
        if (data?.taxes?.length) {
          handleInputChange([...data?.taxes, parseTaxes(taxDetails)], "taxes");
        } else {
          handleInputChange([parseTaxes(taxDetails)], "taxes");
        }
      }
      onTaxDetailsModalClose();
      return;
    }
    let taxErrMsgMap: any = {};
    if (!taxDetails?.taxTypeId) {
      taxErrMsgMap.taxTypeId = "This field is mandatory.";
    }

    if (!taxDetails?.percentage && taxDetails?.percentage !== 0) {
      taxErrMsgMap.percentage = "This field is mandatory.";
    }
    setTaxErrorMap(taxErrMsgMap);
  };

  const onDeleteTaxIconClick = (taxId: string) => {
    setSelectedTaxTypeId(taxId);
    setOpenTaxDeleteAlertPopup(true);
  };

  const onTaxDelete = (taxId: string) => {
    let taxList = data?.taxes?.filter((taxData: any, index: number) => {
      return taxId !== taxData?.taxTypeId;
    });
    handleInputChange(taxList, "taxes");
    setCurTaxIndex(-1);
    setOpenTaxDeleteAlertPopup(false);
  };

  return (
    <section
      className={`${
        isAddNewMode(modeConfig.type) ? "w-full" : "w-[50%] p-[24px] pt-0"
      }`}
    >
      {!isAddNewMode(modeConfig.type) && (
        <div className="flex items-center justify-between">
          <div className="text-[20px] font-normal py-[24px] capitalize">
            {taxAreaDataMap[taxAreaId]?.name}
          </div>
          {isViewMode(modeConfig?.type) && (
            <div
              className="flex cursor-pointer"
              onClick={() =>
                setModeConfig({
                  ...modeConfig,
                  type: COMPONENT_MODES.editMode,
                })
              }
            >
              <div className="rista-sprite ic-pencil-blue"></div>
              <p className="text-14 text-blue font-semibold ml-4">Edit</p>
            </div>
          )}
        </div>
      )}
      <div
        className={`bg-white p-[24px] ${
          !isAddNewMode(modeConfig.type)
            ? "h-[calc(100vh-243px)] overflow-y-auto pb-[66px]"
            : ""
        }`}
      >
        {renderTextField({
          placeHolder: "Enter Tax Area Name, e.g, Gurugram, Karnataka",
          fieldLabel: "Tax Area Name",
          value: get(data, "name") || "",
          boxWidth: "w-full",
          isMandatory: true,
          key: "name",
          regexCheck: (val: any) => val,
          parentKey: "",
          onSubmit: (value: any) => handleInputChange(value, "name"),
          parentClass: "mb-[6px]",
          maxLength: 30
        })}

        <div className="">
          <div
            className={`${
              data?.taxes?.length
                ? "flex items-center justify-between mb-[24px]"
                : ""
            }`}
          >
            <h1 className="text-[18px] text-[#666666] font-medium">Taxes</h1>
            {!isViewMode(modeConfig?.type) && (
              <div
                className={`text-[#0074FB] font-semibold cursor-pointer ${
                  data?.taxes?.length
                    ? "text-[14px]"
                    : "text-[16px] py-[9px] px-[12px] bg-[#F1F7FE] rounded-[8px] border border-[#ADD3FF] text-center mt-[12px]"
                }`}
                onClick={onAddNewTaxCtaClick}
              >
                + {data?.taxes?.length ? "Add New" : "Add Tax"}
              </div>
            )}
          </div>

          {data?.taxes?.length ? (
            data?.taxes?.map((tax: any, index: number) => {
              return (
                <div
                  key={`tax-${index}`}
                  className={`taxArea-tax-item p-[16px] pt-[12px] border border-[#EBEBEB] ${
                    !isViewMode(modeConfig.type) ? "hover:border-black" : ""
                  } rounded-[8px] mb-[12px]`}
                >
                  <div className="mb-[4px]">
                    <div className="flex items-center justify-between">
                      <h1 className="text-[16px] font-semibold">{`${
                        taxTypeDataMap[tax?.taxTypeId]?.name
                      }, ${tax?.percentage}%`}</h1>
                      {!isViewMode(modeConfig?.type) && (
                        <div className="flex items-center">
                          <div
                            className="edit-icon rista-sprite ic-pencil-black hidden mr-[8px]"
                            onClick={() => onEditTaxIconClick(tax, index)}
                          ></div>
                          <div
                            className="delete-icon rista-sprite ic-delete-grey hidden"
                            onClick={() => onDeleteTaxIconClick(tax?.taxTypeId)}
                          ></div>
                        </div>
                      )}
                    </div>
                    {tax?.derivedTaxes?.length
                      ? tax?.derivedTaxes.map(
                          (derivedTax: any, ind: number) => {
                            return (
                              <p
                                key={`derived-tax-${ind}`}
                                className="text-[13px] text-[#4B4B4B] font-normal"
                              >{`${derivedTax?.name}, ${derivedTax?.percentage} %`}</p>
                            );
                          }
                        )
                      : null}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-[#9A3131] text-[12px] font-normal mt-[4px]">
              {errorMessageMap["taxes"]}
            </p>
          )}
        </div>
      </div>
      {modeConfig.type === COMPONENT_MODES.editMode && (
        <div className="fixed bottom-[0px] z-[111] bg-white h-[70px] px-24 flex justify-center items-center overflow-hidden right-0 w-[calc(50%-48px)] sm:w-full border-t-[1px] border-[#f5f5f5]">
          <CtaComponent
            ctaText={"Discard"}
            ctaType={CTA_TYPE.secondary_cta}
            ctaSize={CTA_SIZE.medium}
            onCtaClick={onDiscardCtaClick}
            customClass={"w-[calc(50%-10px)] mr-10"}
          ></CtaComponent>
          <CtaComponent
            ctaText={"Save Changes"}
            ctaType={CTA_TYPE.active_cta}
            ctaSize={CTA_SIZE.medium}
            onCtaClick={onSaveCtaClick}
            customClass={"w-[calc(50%-10px)] ml-10"}
            isDisabled={!isChangesMade}
          ></CtaComponent>
        </div>
      )}

      {openTaxDetailsModal && (
        <TaxDetailsModal
          data={taxDetails}
          taxAreaDataMap={taxAreaDataMap}
          taxTypeDataMap={taxTypeDataMap}
          onModalClose={onTaxDetailsModalClose}
          handleInputChange={onTaxDetailsChange}
          isTaxEditMode={isTaxEditMode}
          onSaveCtaClick={onSaveTaxCtaClick}
          taxErrorMap={taxErrorMap}
          modeConfig={modeConfig}
        />
      )}

      {openTaxDeleteAlertPopup && (
        <ConfirmationModal
          headingText="Remove Tax?"
          description="This is an irreversible action. Do you still want to continue?"
          onConfirmBtnClick={() => onTaxDelete(selectedTaxTypeId)}
          onModalClose={() => setOpenTaxDeleteAlertPopup(false)}
        />
      )}
    </section>
  );
};

export default TaxAreaDetailsForm;
